import React from 'react';
import { ArrowLeft, Star, TrendingUp } from 'lucide-react';
import { Database } from '../lib/supabase';
import { useTheme } from '../hooks/useTheme';

type Child = Database['public']['Tables']['children']['Row'];

interface ChildSelectorProps {
  children: Child[];
  onSelectChild: (childId: string) => void;
  onGoBack: () => void;
  isDarkMode?: boolean;
}

export function ChildSelector({ children, onSelectChild, onGoBack, isDarkMode: propIsDarkMode }: ChildSelectorProps) {
  const { isDarkMode: hookIsDarkMode, theme } = useTheme();
  const isDarkMode = propIsDarkMode !== undefined ? propIsDarkMode : hookIsDarkMode;

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-[var(--bg-primary)]">
      <div className="max-w-4xl w-full">
        <div className="text-center mb-12">
          <div className="flex items-center justify-between mb-6">
            <button
              onClick={onGoBack}
              className="inline-flex items-center space-x-2 px-4 py-2 text-sm transition-all duration-300 rounded-full border shadow-sm backdrop-blur-sm text-blue-600 hover:text-blue-700 bg-white/60 hover:bg-blue-50 border-blue-200 hover:border-blue-300 hover:shadow-md"
            >
              <ArrowLeft className="w-4 h-4" />
              <span>Back to Portal</span>
            </button>
          </div>
          
          <h2 className="text-5xl font-bold mb-4 text-[var(--text-primary)]">
            Who's Learning Today?
          </h2>
          <p className="text-2xl text-[var(--text-secondary)]">
            Choose your profile to start your adventure!
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {children.map((child) => {
            const level = Math.floor(child.total_points / 100) + 1;
            
            return (
              <div
                key={child.id}
                onClick={() => onSelectChild(child.id)}
                className={`group cursor-pointer rounded-3xl p-8 shadow-xl border-4 border-transparent hover:border-blue-300 transition-all duration-300 transform hover:scale-105 hover:shadow-2xl bg-[var(--bg-secondary)] ${
                  theme.reducedMotion ? '' : 'hover:scale-105'
                }`}
              >
                <div className="text-center">
                  <div
                    className={`w-24 h-24 rounded-full flex items-center justify-center text-white font-bold text-3xl mx-auto mb-6 shadow-lg transition-transform duration-300 ${
                      theme.reducedMotion ? '' : 'group-hover:scale-110'
                    }`}
                    style={{ backgroundColor: child.avatar_color }}
                  >
                    {child.name.charAt(0).toUpperCase()}
                  </div>
                  
                  <h3 className="text-3xl font-bold mb-2 text-[var(--text-primary)]">
                    {child.name}
                  </h3>
                  <p className="text-xl mb-6 text-[var(--text-secondary)]">
                    Age {child.age}
                  </p>
                  
                  <div className="space-y-4">
                    <div className="flex items-center justify-center space-x-3 rounded-2xl p-4 bg-[var(--bg-tertiary)] border border-[var(--border-color)]">
                      <Star className="w-6 h-6 text-yellow-500" />
                      <span className="text-xl font-semibold text-[var(--text-primary)]">
                        {child.total_points} points
                      </span>
                    </div>
                    
                    <div className="flex items-center justify-center space-x-3 rounded-2xl p-4 bg-[var(--bg-tertiary)] border border-[var(--border-color)]">
                      <TrendingUp className="w-6 h-6 text-blue-500" />
                      <span className="text-xl font-semibold text-[var(--text-primary)]">
                        Level {level}
                      </span>
                    </div>
                  </div>
                  
                  <div className="mt-6 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-2xl py-4 px-6 font-bold text-xl group-hover:from-blue-600 group-hover:to-purple-700 transition-all duration-300">
                    Start Learning!
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}